import type { APPayload } from './types';

export interface ServiceInvoiceLineState {
  description: string;
  service_item_id: string;
  service_item_code: string;
  service_item_desc: string;
  code_combination_id: string;
  code_combination_value: string;
  code_combination_desc: string;
  amount: string;
  taxable_amount: string;
  non_taxable_amount: string;
  stax_checked: boolean;
  stax_group_id: string;
  stax_group_desc: string;
  stax_rate: string;
  tax_amount: string;
  suspended_stax_group_id?: string;
  suspended_stax_group_desc?: string;
  suspended_stax_rate?: string;
  suspended_tax_amount?: string;
}

export interface ServiceInvoiceFormState {
  service_book_id: string;
  service_book_name: string;
  service_item_id: string;
  service_item_code: string;
  service_item_desc: string;
  code_combination_id: string;
  code_combination_value: string;
  code_combination_desc: string;
  supplier_invoice_ref: string;
  gross_amount: string;
  invoice_date: string;
  payment_term_id: string;
  taxable_amount: string;
  non_taxable_amount: string;
  stax_checked: boolean;
  stax_group_id: string;
  tax_amount: string;
  note: string;
  service_lines: ServiceInvoiceLineState[];
}

const DEFAULT_SERVICE_PAYMENT_TERM_ID = '2';

export function firstValue(...values: unknown[]): unknown {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return null;
}

function toIsoDate(value: unknown): string {
  if (!value) return '';
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

function recordValue(record: Record<string, unknown> | null, key: string): unknown {
  return record ? record[key] : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function bookIdFromAccountCode(value: unknown): string {
  // Finance-provided service book split: 1-5 accounts are Unit 5, 1-9 accounts cover Units 1/2/4.
  const code = String(value ?? '').trim();
  if (code.startsWith('1-5-')) return '5100072';
  if (code.startsWith('1-9-')) return '71';
  return '';
}

function bookNameFromBookId(value: unknown): string {
  const id = String(value ?? '').trim();
  if (id === '5100072') return 'Purchase Invoice Services Socks';
  if (id === '71') return 'Services Purchase Invoice3';
  return '';
}

function firstLineDescription(payload: APPayload): string {
  const line = payload.line_items?.[0];
  return String(
    firstValue(line?.description, line?.item_name, line?.['ITEM_DESC']) ?? '',
  );
}

function stringValue(value: unknown): string {
  return String(value ?? '');
}

function roundUpString(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? String(Math.ceil(n)) : String(value);
}

function paymentTermValue(value: unknown): unknown {
  return String(value ?? '').trim() === '1' ? DEFAULT_SERVICE_PAYMENT_TERM_ID : value;
}

function lineFromRecord(
  value: Record<string, unknown>,
  fallback: Partial<ServiceInvoiceLineState> = {},
): ServiceInvoiceLineState {
  const taxable = firstValue(value.taxable_amount, fallback.taxable_amount);
  const nonTaxable = firstValue(value.non_taxable_amount, fallback.non_taxable_amount);
  const amount = firstValue(value.amount, taxable, nonTaxable, fallback.amount);
  const taxAmount = firstValue(value.tax_amount, fallback.tax_amount);
  const staxChecked =
    typeof value.stax_checked === 'boolean'
      ? value.stax_checked
      : typeof fallback.stax_checked === 'boolean'
        ? fallback.stax_checked
        : Number(taxAmount || 0) > 0;
  const staleUncheckedTax = !staxChecked && Number(taxAmount || 0) > 0;
  return {
    description: stringValue(firstValue(value.description, value.service_description, fallback.description)),
    service_item_id: stringValue(firstValue(value.service_item_id, fallback.service_item_id, '2010589')),
    service_item_code: stringValue(firstValue(value.service_item_code, fallback.service_item_code)),
    service_item_desc: stringValue(firstValue(value.service_item_desc, fallback.service_item_desc)),
    code_combination_id: stringValue(firstValue(value.code_combination_id, fallback.code_combination_id)),
    code_combination_value: stringValue(
      firstValue(value.code_combination_value, fallback.code_combination_value),
    ),
    code_combination_desc: stringValue(
      firstValue(value.code_combination_desc, fallback.code_combination_desc),
    ),
    amount: roundUpString(amount),
    taxable_amount: roundUpString(taxable),
    non_taxable_amount: staleUncheckedTax ? '' : roundUpString(nonTaxable),
    stax_checked: staxChecked,
    stax_group_id: staxChecked
      ? stringValue(firstValue(value.stax_group_id, fallback.stax_group_id))
      : '',
    stax_group_desc: staxChecked
      ? stringValue(firstValue(value.stax_group_desc, fallback.stax_group_desc))
      : '',
    stax_rate: staxChecked ? stringValue(firstValue(value.stax_rate, fallback.stax_rate)) : '',
    tax_amount: staxChecked ? roundUpString(taxAmount) : '',
  };
}

function serviceLinesFromPayload(
  payload: APPayload,
  pf: APPayload['service_preflight'] | null,
  fallback: Partial<ServiceInvoiceLineState>,
): ServiceInvoiceLineState[] {
  const pfRecord = asRecord(pf);
  const pfLines = Array.isArray(pfRecord?.service_lines) ? pfRecord.service_lines : [];
  const structuredLines = pfLines
    .map((line) => asRecord(line))
    .filter((line): line is Record<string, unknown> => line !== null);
  if (structuredLines.length > 0) {
    return structuredLines.map((line) => lineFromRecord(line, fallback));
  }

  const ocrLines = Array.isArray(payload.line_items) ? payload.line_items : [];
  const mapped = ocrLines
    .map((line) => asRecord(line))
    .filter((line): line is Record<string, unknown> => line !== null)
    .map((line) =>
      lineFromRecord(
        {
          description: firstValue(line.description, line.item_name, line.ITEM_DESC),
          non_taxable_amount: firstValue(line.amount, line.AMOUNT),
        },
        fallback,
      ),
    );
  if (mapped.length > 0) return mapped;
  return [lineFromRecord({ description: firstLineDescription(payload) }, fallback)];
}

export function defaultsFrom(payload: APPayload): ServiceInvoiceFormState {
  const structured = asRecord((payload as Record<string, unknown>)['structured_fields']);
  const pf =
    (asRecord(recordValue(structured, 'service_preflight')) as APPayload['service_preflight']) ??
    payload.service_preflight ??
    null;
  const learned = asRecord((payload as Record<string, unknown>)['service_preflight_defaults']);
  // Bucket-contract rows put OCR results under payload.default_fields.*; legacy
  // rows put them flat on payload. Read default_fields first, then fall back
  // to the flat keys, so the modal autofills for both shapes.
  const df = asRecord((payload as Record<string, unknown>)['default_fields']);
  const codeValue = String(
    firstValue(
      pf?.code_combination_value,
      recordValue(learned, 'code_combination_value'),
      (payload as Record<string, unknown>)['code_combination_value'],
    ) ?? '',
  );
  const serviceBookId = String(
    firstValue(
      pf?.service_book_id,
      recordValue(learned, 'service_book_id'),
      bookIdFromAccountCode(codeValue),
    ) ?? '',
  );
  const taxAmount = String(
    firstValue(pf?.tax_amount, recordValue(df, 'tax_amount'), payload.tax_amount) ?? '',
  );
  const subtotal = firstValue(
    recordValue(df, 'subtotal_before_tax'),
    payload.subtotal_before_tax,
    payload.subtotal,
  );
  const grossAmount = firstValue(
    recordValue(df, 'gross_amount'),
    payload.gross_amount,
    payload.amount,
  );
  const staxChecked =
    typeof pf?.stax_checked === 'boolean'
      ? Boolean(pf.stax_checked)
      : typeof recordValue(learned, 'stax_checked') === 'boolean'
        ? Boolean(recordValue(learned, 'stax_checked'))
        : Number(taxAmount || 0) > 0;
  const serviceItemId = String(
    firstValue(pf?.service_item_id, recordValue(learned, 'service_item_id'), '2010589') ?? '',
  );
  const serviceItemCode = String(
    firstValue(pf?.service_item_code, recordValue(learned, 'service_item_code')) ?? '',
  );
  const serviceItemDesc = String(
    firstValue(pf?.service_item_desc, recordValue(learned, 'service_item_desc')) ?? '',
  );
  const codeCombinationId = String(
    firstValue(pf?.code_combination_id, recordValue(learned, 'code_combination_id')) ?? '',
  );
  const codeCombinationDesc = String(
    firstValue(pf?.code_combination_desc, recordValue(learned, 'code_combination_desc')) ?? '',
  );
  const fallbackDescription = String(pf?.service_description ?? firstLineDescription(payload));
  const fallbackLine: Partial<ServiceInvoiceLineState> = {
    description: fallbackDescription,
    service_item_id: serviceItemId,
    service_item_code: serviceItemCode,
    service_item_desc: serviceItemDesc,
    code_combination_id: codeCombinationId,
    code_combination_value: codeValue,
    code_combination_desc: codeCombinationDesc,
    taxable_amount: roundUpString(
      firstValue(
        pf?.taxable_amount,
        recordValue(learned, 'taxable_amount'),
        staxChecked ? subtotal : null,
      ),
    ),
    non_taxable_amount: roundUpString(
      firstValue(
        pf?.non_taxable_amount,
        recordValue(learned, 'non_taxable_amount'),
        staxChecked ? null : firstValue(subtotal, grossAmount),
      ),
    ),
    stax_checked: staxChecked,
    stax_group_id: String(pf?.stax_group_id ?? ''),
    tax_amount: roundUpString(taxAmount),
  };
  const serviceLines = serviceLinesFromPayload(payload, pf, fallbackLine);

  return {
    service_book_id: serviceBookId,
    service_book_name: String(
      firstValue(
        pf?.service_book_name,
        recordValue(learned, 'service_book_name'),
        bookNameFromBookId(serviceBookId),
      ) ?? '',
    ),
    service_item_id: serviceItemId,
    service_item_code: serviceItemCode,
    service_item_desc: serviceItemDesc,
    code_combination_id: codeCombinationId,
    code_combination_value: codeValue,
    code_combination_desc: codeCombinationDesc,
    supplier_invoice_ref: String(
      firstValue(
        pf?.supplier_invoice_ref,
        recordValue(df, 'invoice_no'),
        payload.invoice_no,
      ) ?? '',
    ),
    gross_amount: roundUpString(firstValue(pf?.gross_amount, grossAmount)),
    invoice_date: toIsoDate(
      firstValue(pf?.invoice_date, recordValue(df, 'invoice_date'), payload.invoice_date),
    ),
    payment_term_id: String(
      paymentTermValue(
        firstValue(
          pf?.payment_term_id,
          DEFAULT_SERVICE_PAYMENT_TERM_ID,
        ),
      ) ?? '',
    ),
    taxable_amount: roundUpString(
      firstValue(
        pf?.taxable_amount,
        recordValue(learned, 'taxable_amount'),
        staxChecked ? subtotal : null,
      ),
    ),
    non_taxable_amount: roundUpString(
      firstValue(
        pf?.non_taxable_amount,
        recordValue(learned, 'non_taxable_amount'),
        staxChecked ? null : firstValue(subtotal, grossAmount),
      ),
    ),
    stax_checked: staxChecked,
    stax_group_id: String(pf?.stax_group_id ?? ''),
    tax_amount: roundUpString(taxAmount),
    note: '',
    service_lines: serviceLines,
  };
}
